import { Navigate } from 'react-router-dom'
import { useUser } from '@/contexts/UserContext'
import { workspaceHome, isWorkspaceOnlyUser } from '@/lib/workspaceHome'

interface WorkspaceHomeRouteProps {
  /** What to render for users who do belong in the CRM. */
  children: React.ReactNode
}

/**
 * Guards `/`.
 *
 * The CRM dashboard was mounted at `/` with no protection whatsoever, so every
 * authenticated account could reach it regardless of type — HR-only `Employee`
 * users included, before Training existed. `ProtectedRoute` was no help because
 * its own denial redirect pointed at `/`, which is how a refused user ended up on
 * the very page they were being refused.
 *
 * Accounts that have their own workspace are sent there instead. Everyone else
 * gets the dashboard as before.
 */
export function WorkspaceHomeRoute({ children }: WorkspaceHomeRouteProps) {
  const { userType, impersonatedUser } = useUser()

  // Effective type, so an admin impersonating a trainer sees what the trainer
  // sees rather than the dashboard.
  const effectiveType = impersonatedUser?.user_type || userType

  if (isWorkspaceOnlyUser(effectiveType)) {
    return <Navigate to={workspaceHome(effectiveType)} replace />
  }

  return <>{children}</>
}
